import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AdCampaignModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/adCampaign/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/adCampaign/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/adCampaign/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/adCampaign/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/adCampaign/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/adCampaign/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/adCampaign/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/adCampaign/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/adCampaign/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/adCampaign/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/adCampaign/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/adCampaign/DELETE_FAILURE'

export type AdCampaignModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AdCampaignModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAdCampaigns(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().adCampaigns.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/ad-campaigns', options)
      .then(({ data }: { data: { data: AdCampaignModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.AD_CAMPAIGN_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAdCampaign(adCampaign: AdCampaignModel, options: EntityOptions = {}) {
  const { adCampaignId, adChannelTypeId } = adCampaign

  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().adCampaigns.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/ad-campaigns/${adChannelTypeId}/${adCampaignId}`, options)
      .then(({ data }: { data: AdCampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_CAMPAIGN)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAdCampaign(
  adCampaign: AdCampaignModel,
  options: EntityOptions = {},
) {
  const {
    adChannelTypeId,
    adCampaignId,
    spend: {
      budget: {
        value,
      },
    },
  } = adCampaign

  const config = {
    method: 'PATCH',
    data: JSON.stringify({
      budget: value,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/ad-campaigns/${adChannelTypeId}/${adCampaignId}`, options, config)
      .then(({ data }: { data: AdCampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_CAMPAIGN)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAdCampaign(adCampaign: AdCampaignModel) {
  const { adCampaignId, adChannelTypeId } = adCampaign

  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/ad-campaigns/${adChannelTypeId}/${adCampaignId}`, {}, config)
      .then(({ status }) => {
        if (status !== 200) throw new Error()

        const deletedEntity = { id: adCampaign.id }
        const normalizedJson = normalize(deletedEntity, Schemas.AD_CAMPAIGN)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data: deletedEntity }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

export function duplicateAdCampaign(adCampaign: AdCampaignModel, options: EntityOptions = {}) {
  const { adQueueId } = adCampaign

  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    // Note: duplication of AdCampaign is done via AdQueues API
    const promise = api(`/ad-queues/${adQueueId}/duplicate`, options, config)
      .then(({ data }: { data: AdCampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_QUEUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAdCampaignStatus(adCampaign: AdCampaignModel, status: string) {
  const { adCampaignId, adChannelTypeId } = adCampaign

  const config = {
    method: 'PUT',
    data: JSON.stringify({
      status,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/ad-campaigns/${adChannelTypeId}/${adCampaignId}/status`, {}, config)
      .then(({ data }: { data: Partial<AdCampaignModel> }) => {
        const adCampaignToUpdate = {
          id: `${adChannelTypeId}${adCampaignId}`,
          ...data,
        }

        const normalizedJson = normalize(adCampaignToUpdate, Schemas.AD_CAMPAIGN)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function syncAdCampaign(adCampaign: AdCampaignModel, options: EntityOptions = {}) {
  const { adCampaignId, adChannelTypeId } = adCampaign

  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/ad-campaigns/${adChannelTypeId}/${adCampaignId}/synchronize`, options, config)
      .then(({ data }: { data: AdCampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_CAMPAIGN)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AdCampaignModuleState = initialState,
  action: Action = { type: '' },
): AdCampaignModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
